import { ActionReducerMap, createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromContracts from '@appStore/contracts/reducers/contracts.reducer';
import * as fromBudgets from '@appStore/contracts/reducers/budgets.reducer';
import * as fromAgreements from '@appStore/contracts/reducers/agreements.reducer';
import * as fromRoot from '../../../reducers';
import { MeteringPointsState } from '@appStore/metering-points/reducers';
import { Contract, AgreementAddition, Agreement, ProductSubscription } from '@model';
import { getAgreementState } from '@appStore/products';
import { CustomersState } from '@appStore/customers';

// feature state
export interface ContractsState {
  contracts: fromContracts.State;
  budgets: fromBudgets.State;
  agreements: fromAgreements.State;
}

export interface State extends fromRoot.State {
  contracts: ContractsState;
}

// reducers
export const reducers: ActionReducerMap<ContractsState> = {
  contracts: fromContracts.reducer,
  budgets: fromBudgets.reducer,
  agreements: fromAgreements.reducer
};

export const getState = createFeatureSelector<ContractsState>('contracts');
export const getContractsState = createSelector(
  getState,
  (state: ContractsState) => state.contracts
);

// Contracts
export const getContracts = createSelector(
  getContractsState,
  fromContracts._getContracts
);
export const getContractsLoaded = createSelector(
  getContractsState,
  fromContracts._getContractsLoaded
);
export const getContractsLoading = createSelector(
  getContractsState,
  fromContracts._getContractsLoading
);

export const getContractLoaded = createSelector(
  getContractsState,
  fromContracts._getContractLoaded
);

export const getContractsForDateIntervalLoaded = createSelector(
  getContractsState,
  fromContracts._getContractsForDateIntervalLoaded
);

// Selected Contract Tab
export const getSelectedTabIndexContract = createSelector(
  getContractsState,
  fromContracts._getSelectedTabIndexContract
);

// Contracts Filter
export const getContractsFilter = createSelector(
  getContractsState,
  fromContracts._getContractsFilter
);


// Agreement products
export const getAgreementProducts = createSelector(
  getContractsState,
  fromContracts._getAgreementProducts
);
export const getAgreementProductsLoaded = createSelector(
  getContractsState,
  fromContracts._getAgreementProductsLoaded
);
export const getAgreementProductsLoading = createSelector(
  getContractsState,
  fromContracts._getAgreementProductsLoading
);
// Agreement addition product exclusions
export const getAgreementAdditionProductExclusions = createSelector(
  getContractsState,
  fromContracts._getAgreementAdditionProductExclusions
);
export const getAgreementAdditionProductExclusionsLoaded = createSelector(
  getContractsState,
  fromContracts._getAgreementAdditionProductExclusionsLoaded
);
export const getAgreementAdditionProductExclusionsLoading = createSelector(
  getContractsState,
  fromContracts._getAgreementAdditionProductExclusionsLoading
);
// Selected Tab
export const getSelectedTab = createSelector(
  getContractsState,
  fromContracts.getCustomerSelectedTab
);
export const getSelectedColdPageIndex = createSelector(
  getContractsState,
  fromContracts._getSelectedColdPageIndex
);
export const getSelectedHotPageIndex = createSelector(
  getContractsState,
  fromContracts._getSelectedHotPageIndex
);
export const getHasContractAdditions = createSelector(
  getContractsState,
  fromContracts._getHasContractAdditions
);
export const getHasContractAdditionsLoaded = createSelector(
  getContractsState,
  fromContracts._getHasContractAdditionsLoaded
);

// Budgets
export const getBudgetsState = createSelector(
  getState,
  (state: ContractsState) => state.budgets
);

export const getBudgets = createSelector(
  getBudgetsState,
  fromBudgets._getBudgets
);
export const getBudgetLoaded = createSelector(
  getBudgetsState,
  fromBudgets._getBudgetLoaded
);
export const getBudgetLoading = createSelector(
  getBudgetsState,
  fromBudgets._getBudgetLoading
);
export const getBudgetFailed = createSelector(
  getBudgetsState,
  fromBudgets._getBudgetFailed
);

export const getSelectedBudget = createSelector(
  getBudgetsState,
  fromBudgets._getSelectedBudget
);

export const getSelectedPriceListCode = createSelector(
  getBudgetsState,
  fromBudgets._getSelectedPriceListCode
);
// Prognosis
export const getPrognosis = createSelector(
  getBudgetsState,
  fromBudgets._getPrognosis
);
export const getPrognosisLoaded = createSelector(
  getBudgetsState,
  fromBudgets._getPrognosisLoaded
);
export const getPrognosisLoading = createSelector(
  getBudgetsState,
  fromBudgets._getPrognosisLoading
);
export const getPrognosisFailed = createSelector(
  getBudgetsState,
  fromBudgets._getPrognosisFailed
);
// Selected Prognosis Tab
export const _getSelectedTabIndexPrognosis = createSelector(
  getBudgetsState,
  fromBudgets._getSelectedTabIndexPrognosis
);

// Agreements 
export const getAgreementsState = createSelector(
  getState,
  (state: ContractsState) => state.agreements
);

export const getAdditionAgreementCreated = createSelector(
  getAgreementsState,
  fromAgreements._getAdditionAgreementCreated
);

export const getAdditionAgreementCreateId = createSelector(
  getAgreementsState,
  fromAgreements._getAdditionAgreementCreateId
);

export const getActiveAgreementLink = createSelector(
  getAgreementsState,
  fromAgreements._getActiveAgreementLink
)
export const getActiveAgreementLinkLoaded = createSelector(
  getAgreementsState,
  fromAgreements._getActiveAgreementLinkLoaded
)

export const getIsAdditionAgreementCreated = createSelector(
  getAgreementsState,
  fromAgreements._getIsAdditionAgreementCreated
)
export const getIsAdditionAgreementCreatedLoaded = createSelector(
  getAgreementsState,
  fromAgreements._getIsAdditionAgreementCreatedLoaded
)


// Agreement addition esign
export const getAgreementAdditionEsign = createSelector(
  getContractsState,
  fromContracts._getAgreementAdditionEsign
);

export const getAgreementAdditionEsignLoaded = createSelector(
  getContractsState,
  fromContracts._getAgreementAdditionEsignLoaded
);

// Combined selectors
const getCustomersFeatureState = createFeatureSelector<CustomersState>('customers');
const getCustomersState = createSelector(
  getCustomersFeatureState,
  state => state.customers
);

const getMeteringPointFeatureState = createFeatureSelector<MeteringPointsState>('meteringpoints');
const getMeteringPointsState = createSelector(
  getMeteringPointFeatureState,
  state => state.meteringPoints
);

export const getContractsWithMeteringPoints = createSelector(
  getMeteringPointsState,
  getContractsState,
  (mpState, cState): Contract[] => {
    if (!mpState.loaded || !cState.contractsLoaded || !cState.agreementProductsLoaded) {
      return;
    }

    let additionalProducts = [] as AgreementAddition[];
    cState.agreementProducts.forEach(
      ap => (additionalProducts = [...additionalProducts, ...ap.additionalProducts.map(adp => ({ ...adp }))])
    );

    return cState.contracts.map(c => {
      let product = cState.agreementProducts.find(ap => ap.code === c.productCode);

      return {
        ...c,
        meteringPoint: mpState.data.find(mp => mp.id === c.meteringPointId),
        productName: product ? product.name : c.productName ? c.productName : c.productCode,
        additionalProducts: c.additionalProducts.map(adp => {
          let additionalProduct = null;
          if (adp.sublevel) {
            additionalProduct = additionalProducts.find(
              ap => adp.sublevel && ap.code === adp.code && ap.subComponents.some(sc => sc.endsWith(adp.sublevel))
            );
          } else {
            additionalProduct = additionalProducts.find(
              ap =>
                // Detta blir fel...
                ap.code === adp.code || (!adp.sublevel && ap.code === adp.code)
            );
          }

          return {
            ...adp,
            name: adp.option !== false && additionalProduct ? additionalProduct.name : adp.name ? adp.name : adp.code
          };
        })
      };
    });
  }
);

export const getSelectedContract = createSelector(
  getCustomersState,
  getMeteringPointsState,
  getContractsState,
  (customerState, mpState, contractState): Contract => {
    if (!customerState.loaded || !mpState.loaded || !contractState.contractLoaded || !contractState.selectedContract) {
      return;
    }

    return {
      ...contractState.selectedContract,
      customerSegment: customerState.data.some(c => c.id === contractState.selectedContract.customerId) ? 
                          customerState.data.find(c => c.id === contractState.selectedContract.customerId).customerSegment :
                          0,
      meteringPoint: mpState.data.find(mp => mp.id === contractState.selectedContract.meteringPointId)
    };
  }
);

export const getContractsForDateInterval = createSelector(
  getCustomersState,
  getMeteringPointsState,
  getContractsState,
  (customerState, mpState, contractState): Contract[] => {
    if (!customerState.loaded || !mpState.loaded || !contractState.contractsForDateIntervalLoaded || !contractState.contractsForDateInterval) {
      return;
    }

    return contractState.contractsForDateInterval;
  }
);


export const getSelectedAgreement = createSelector(
  getContractsState,
  (contractState): Agreement => {
    if (!contractState.agreementProductsLoaded || !contractState.contractLoaded || !contractState.selectedContract) {
      return;
    }

    return contractState.agreementProducts.find(ap => ap.code === contractState.selectedContract.standardProductCode);
  }
);

export const getSelectedAgreementNewContract = createSelector(
  getContractsState,
  (contractState): Agreement => {
    if (!contractState.agreementProductsLoaded || !contractState.contractLoaded || !contractState.selectedContract) {
      return;
    }

    return contractState.agreementProducts.find(ap => ap.code === contractState.selectedContract.newContract?.standardProductCode);
  }
);
